<template>
  <div>
    <Spinner v-if="loading" />
    <ul class="nav nav-tabs" role="tablist">
      <Tab v-for="(priceGroup, index) in servicePriceGroupList" v-bind:first="index == 0" :tabid="`tab-service-production-override-${index}`" :key="index">
        {{priceGroup.name}}
      </Tab>
    </ul>
    <div class="tab-content">
      <TabPage v-for="(priceGroup, index) in servicePriceGroupList" v-bind:first="index == 0" :tabid="`tab-service-production-override-${index}`" :key="index">
        <StaticDataTable class="static-table-list" v-show="!loading && !loadingError">
          <template v-slot:header>
            <tr>
              <th width="20%">Price Type</th>
              <th width="40%">Production Stages</th>
              <th width="40%">Production Stations</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="priceRange in priceGroup.servicePriceRanges" :key="priceRange.id">
              <td class="font-weight-bold">
                {{priceRange.name}}
              </td>
              <td v-if="!editing">
                <template v-for="(stage, stageIndex) of stageListForPriceGroupRange(priceGroup.id, priceRange.id)">
                  <span v-if="stageIndex > 0" :key="`stage-sep-${stage.id}`">, </span>
                  <LinkProductionStage :key="stage.id" :id="stage.id" :name="stage.name" />
                </template>
                <span v-if="stageListForPriceGroupRange(priceGroup.id, priceRange.id).length == 0" class="font-italic text-muted">Default</span>
              </td>
              <td v-if="!editing">
                <template v-for="(station, stationIndex) of stationListForPriceGroupRange(priceGroup.id, priceRange.id)">
                  <span v-if="stationIndex > 0" :key="`station-sep-${station.id}`">, </span>
                  <LinkProductionStation :key="station.id" :id="station.id" :name="station.name" />
                </template>
                <span v-if="stationListForPriceGroupRange(priceGroup.id, priceRange.id).length == 0" class="font-italic text-muted">Default</span>
              </td>

              <td v-if="editing">
                <FormMultiSelect
                  :value="convertListToIDs(stageListForPriceGroupRange(priceGroup.id, priceRange.id))"
                  :options="productionStageLookup"
                  @input="updateStageListForPriceGroupRange(priceGroup.id, priceRange.id, $event)"
                  placeholder="Select stage overrides" />
              </td>
              <td v-if="editing">
                <FormMultiSelect
                  :value="convertListToIDs(stationListForPriceGroupRange(priceGroup.id, priceRange.id))"
                  :options="productionStationLookup"
                  @input="updateStationListForPriceGroupRange(priceGroup.id, priceRange.id, $event)"
                  placeholder="Select station overrides" />
              </td>

            </tr>
          </template>
        </StaticDataTable>
      </TabPage>
    </div>
    <span class="text-danger" v-if="loadingError">{{loadingError}}</span>
  </div>
</template>


<script>
import _ from 'lodash'
import formMixin from '@/mixins/form'

export default {
  name: 'ProductionGlassServiceStockLinkEditor',
  model: {
    event: 'update'
  },
  mixins: [formMixin],
  props: {
    editing: Boolean,
    stockId: Number,
    value: Object
  },
  data() {
    return {
      loading: false,
      loadingError: '',
      priceGroupList: [],
      productionStageList: [],
      productionStationList: [],
      stockProductionConfig: _.cloneDeep(this.$props.value || {})
    }
  },
  computed: {
    servicePriceGroupList() {
      // Filter out price groups with no service ranges
      return this.priceGroupList.filter(priceGroup => priceGroup.servicePriceRanges.length > 0)
    },
    productionStageLookup() {
      return this.productionStageList.map(stage => {
        return {
          value: stage.id,
          name: stage.name,
          colour: stage.highlightColour
        }
      })
    },
    productionStationLookup() {
      return this.productionStationList.map(station => {
        return {
          value: station.id,
          name: station.name
        }
      })
    }
  },
  watch: {
    value: function() {
      this.$data.stockProductionConfig = _.cloneDeep(this.$props.value)
    }
  },
  mounted() {
    this.requestData()
  },
  methods: {
    requestData() {
      this.loading = true
      this.loadingError = ''

      Promise.all([
        this.$apiService.pricingService.getStockPriceGroupList(),
        this.$apiService.productionService.getProductionStageList(),
        this.$apiService.productionService.getProductionStationList()
      ])
      .then(results => {
        this.priceGroupList = results[0].data
        this.productionStageList = results[1].data
        this.productionStationList = results[2].data
      })
      .catch(err => {
        this.loadingError = 'Failed to load data'
      })
      .finally(() => {
        this.loading = false
      })
    },

    stageListForPriceGroupRange(stockPriceGroupId, servicePriceRangeId) {
      if(!this.stockProductionConfig || !this.stockProductionConfig.productionStageOverrideList)
        return []

      return this.stockProductionConfig.productionStageOverrideList
      .filter(configData => configData.stockPriceGroupId == stockPriceGroupId && configData.servicePriceRangeId == servicePriceRangeId)
      .map(configData => {
        let stageInfo = this.productionStageList.find(stage => stage.id == configData.productionStageId)
        return {
          id: configData.productionStageId,
          value: configData.productionStageId,
          name: stageInfo ? stageInfo.name : '',
        }
      })
    },
    updateStageListForPriceGroupRange(stockPriceGroupId, servicePriceRangeId, productionStageList) {
      if(!this.stockProductionConfig.productionStageOverrideList)
        this.stockProductionConfig.productionStageOverrideList = []

      // Clear out the price group settings
      this.stockProductionConfig.productionStageOverrideList = this.stockProductionConfig.productionStageOverrideList.filter(override => {
        return override.stockPriceGroupId != stockPriceGroupId || override.servicePriceRangeId != servicePriceRangeId
      })

      // Add the stages
      this.stockProductionConfig.productionStageOverrideList = _.concat(this.stockProductionConfig.productionStageOverrideList, productionStageList.map(stageId => {
        return {
          productionStageId: stageId,
          stockPriceGroupId: stockPriceGroupId,
          servicePriceRangeId: servicePriceRangeId,
          stockId: this.stockId
        }
      }))

      this.$emit('update', this.stockProductionConfig)
    },

    stationListForPriceGroupRange(stockPriceGroupId, servicePriceRangeId) {
      if(!this.stockProductionConfig || !this.stockProductionConfig.productionStationOverrideList)
        return []

      return this.stockProductionConfig.productionStationOverrideList
      .filter(configData => configData.stockPriceGroupId == stockPriceGroupId && configData.servicePriceRangeId == servicePriceRangeId)
      .map(configData => {
        let stationInfo = this.productionStationList.find(station => station.id == configData.productionStationId)
        return {
          id: configData.productionStationId,
          value: configData.productionStationId,
          name: stationInfo ? stationInfo.name : ''
        }
      })
    },
    updateStationListForPriceGroupRange(stockPriceGroupId, servicePriceRangeId, productionStationList) {
      if(!this.stockProductionConfig.productionStationOverrideList)
        this.stockProductionConfig.productionStationOverrideList = []

      // Clear out the price group settings
      this.stockProductionConfig.productionStationOverrideList = this.stockProductionConfig.productionStationOverrideList.filter(override => {
        return override.stockPriceGroupId != stockPriceGroupId || override.servicePriceRangeId != servicePriceRangeId
      })

      // Add the stations
      this.stockProductionConfig.productionStationOverrideList = _.concat(this.stockProductionConfig.productionStationOverrideList, productionStationList.map(stationId => {
        return {
          productionStationId: stationId,
          stockPriceGroupId: stockPriceGroupId,
          servicePriceRangeId: servicePriceRangeId,
          stockId: this.stockId
        }
      }))

      this.$emit('update', this.stockProductionConfig)
    },
  }


}
</script>